import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ChevronLeft, ChevronRight, Mic, Users, AlertTriangle, CheckCircle2, Coffee, Play } from "lucide-react";
import { api, clearSession, errMsg, getSession } from "@/lib/api";
import { TopBar } from "@/components/fly/Logo";
import { LoadError } from "@/pages/JudgeList";
import { DayTabs } from "@/components/fly/DayTabs";
import { AnnouncerScoredDialog } from "@/components/fly/AnnouncerScoredDialog";

const DAY_KEY = "flycup_ann_day";

export default function Announcer() {
  const nav = useNavigate();
  const s = getSession();
  const [days, setDays] = useState([]);
  const [day, setDay] = useState(localStorage.getItem(DAY_KEY) || "");
  const [items, setItems] = useState([]);
  const [cur, setCur] = useState(null);
  const [block, setBlock] = useState(true);
  const [onBreak, setOnBreak] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [error, setError] = useState("");

  const loadConfig = useCallback(() => {
    api.get("/config").then((r) => {
      setDays(r.data.days);
      if (!r.data.days.includes(day)) setDay(r.data.days[0]);
    }).catch((e) => { setError(errMsg(e)); setTimeout(loadConfig, 4000); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const dayRef = useRef(day);
  const load = useCallback(() => {
    if (!day) return;
    api.get("/announcer/list", { params: { day } })
      .then((r) => { if (dayRef.current === day) { setItems(r.data.performances); setCur(r.data.current_id); setBlock(r.data.block); setOnBreak(r.data.on_break); setError(""); } })
      .catch((e) => { if (dayRef.current === day) setError(errMsg(e)); });
  }, [day]);

  useEffect(() => {
    if (!day) return;
    dayRef.current = day;
    localStorage.setItem(DAY_KEY, day);
    setItems([]);
    setError("");
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [day, load]);

  const idx = useMemo(() => Math.max(0, items.findIndex((p) => p.id === cur)), [items, cur]);
  const [scoredInfo, setScoredInfo] = useState(null);
  const go = async (i, force = false) => {
    const p = items[i];
    if (!p) return;
    if (!force && i !== idx && p.judges_total > 0 && p.judges_scored >= p.judges_total) {
      const fi = items.findIndex((x) => x.judges_scored < x.judges_total);
      setScoredInfo({ i, p, first: fi >= 0 ? { i: fi, p: items[fi] } : null });
      return;
    }
    setScoredInfo(null);
    try {
      await api.put("/announcer/current", { day, performance_id: p.id });
      setCur(p.id);
      load();
    } catch (e) {
      toast.error(errMsg(e), { duration: 8000 });
      load();
    }
  };

  const toggleBreak = async () => {
    try {
      const { data } = await api.put("/announcer/break", { day, on: !onBreak });
      setOnBreak(data.on_break);
      toast.success(data.on_break ? "PRZERWA – ocenianie sędziów wstrzymane" : "Koniec przerwy – sędziowie mogą oceniać");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const firstOpen = useMemo(() => {
    const i = items.findIndex((p) => p.present && p.judges_scored < p.judges_total);
    return Math.min(i < 0 ? items.length : i, idx);
  }, [items, idx]);

  const current = items[idx];
  return (
    <>
      <TopBar title={s?.name} right={
        <button data-testid="ann-logout-btn" onClick={() => { clearSession(); nav("/konferansjer/login"); }} className="h-10 px-3 rounded-lg border border-white/10 text-slate-300 hover:text-white flex items-center gap-2 text-sm"><LogOut className="w-4 h-4" /><span className="hidden sm:inline">Wyloguj</span></button>
      } />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400 flex items-center gap-2"><Mic className="w-4 h-4" />Konferansjer</p>
            <h1 className="font-display text-4xl font-extrabold uppercase">Kolejność wystąpień</h1>
          </div>
          {items.length > 0 && <div data-testid="ann-position" className="font-mono text-sm text-slate-300">Występ <span className="text-white font-bold">{idx + 1}</span> / {items.length}</div>}
        </div>
        <div className="mt-5"><DayTabs days={days} value={day} onChange={setDay} prefix="ann-day" /></div>
        {error && <LoadError msg={error} onRetry={load} />}
        <AnnouncerScoredDialog info={scoredInfo} onClose={() => setScoredInfo(null)} onGo={(i) => go(i, true)} />
        {current && <BreakBar on={onBreak} onToggle={toggleBreak} />}
        {current && <NowCard p={current} block={block} onPrev={() => go(idx - 1)} onNext={() => go(idx + 1)} hasPrev={idx > 0} hasNext={idx < items.length - 1} />}
        {items.length > idx + 1 && (
          <section className="mt-6">
            <h2 className="font-display text-xl font-bold uppercase text-slate-300">Następnie</h2>
            <div className="grid md:grid-cols-3 gap-3 mt-3">
              {items.slice(idx + 1, idx + 4).map((p, i) => <NextCard key={p.id} p={p} n={i + 1} onClick={() => go(idx + 1 + i)} />)}
            </div>
          </section>
        )}
        <section className="mt-8">
          <h2 className="font-display text-xl font-bold uppercase text-slate-300">Pełna lista dnia</h2>
          {firstOpen > 0 && (
            <button data-testid="ann-toggle-done" onClick={() => setShowDone((v) => !v)} className="mt-3 h-10 px-4 rounded-lg border border-white/10 text-sm text-slate-300 hover:bg-white/5">
              {showDone ? "Ukryj ocenione" : `Pokaż wcześniejsze (${firstOpen})`}
            </button>
          )}
          <ul className="mt-3 space-y-1.5" data-testid="ann-list">
            {items.map((p, i) => (showDone || i >= firstOpen) && <ListRow key={p.id} p={p} active={i === idx} done={i < idx} onClick={() => go(i)} />)}
          </ul>
        </section>
      </main>
    </>
  );
}

const BreakBar = ({ on, onToggle }) => (
  <div className={`mt-6 rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${on ? "border-amber-500/60 bg-amber-500/15" : "border-white/10 bg-[#1C2541]"}`} data-testid="ann-break-bar">
    <div className="flex-1 text-sm">
      {on
        ? <span className="text-amber-200"><b className="font-display text-2xl uppercase text-amber-300 mr-2">Przerwa</b>Ocenianie sędziów jest wstrzymane, a w „Teraz na scenie” widzowie widzą PRZERWA.</span>
        : <span className="text-slate-400">Sędziowie oceniają tylko osobę, która jest teraz na scenie.</span>}
    </div>
    <button data-testid="ann-break-btn" onClick={onToggle} className={`h-14 px-6 rounded-xl font-display text-xl font-bold uppercase flex items-center justify-center gap-2 shrink-0 active:scale-95 transition-transform ${on ? "bg-emerald-600 hover:bg-emerald-500" : "bg-amber-600 hover:bg-amber-500"}`}>
      {on ? <><Play className="w-6 h-6" />Koniec przerwy</> : <><Coffee className="w-6 h-6" />Przerwa</>}
    </button>
  </div>
);

const Status = ({ p }) => (
  <div className="flex flex-wrap gap-2">
    {p.present
      ? <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-emerald-500/15 border border-emerald-500/40 text-emerald-300"><CheckCircle2 className="w-3.5 h-3.5" />Zarejestrowany</span>
      : <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-red-500/15 border border-red-500/40 text-red-300"><AlertTriangle className="w-3.5 h-3.5" />Niezarejestrowany</span>}
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-white/5 border border-white/10 text-slate-300 font-mono">Oceny: {p.judges_scored}/{p.judges_total}</span>
    {p.is_team && <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-cyan-500/15 border border-cyan-500/40 text-cyan-300"><Users className="w-3.5 h-3.5" />Duet / grupa</span>}
  </div>
);

const NowCard = ({ p, block, onPrev, onNext, hasPrev, hasNext }) => (
  <section data-testid="ann-now-card" className="mt-6 fly-card p-5 sm:p-8 border-blue-500/50 shadow-[0_0_40px_rgba(37,99,235,0.25)] rise">
    <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-red-400">
      <span className="h-2.5 w-2.5 rounded-full bg-red-500 live-dot" />Teraz na scenie {p.time && <span className="text-slate-400">· {p.time}</span>}
    </div>
    <div data-testid="ann-now-name" className="font-display font-black uppercase text-3xl sm:text-5xl lg:text-6xl leading-[0.95] mt-3 break-words">{p.name}</div>
    <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-base">
      <Info label="Kategoria" value={p.category} />
      <Info label="Wiek" value={p.age_group} />
      <Info label="Klub / szkoła" value={p.school} />
      <Info label="Instruktor" value={p.instructor} />
    </div>
    <div className="mt-5"><Status p={p} /></div>
    {p.present && p.judges_missing?.length > 0 && (
      <div data-testid="ann-missing-judges" className="mt-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /><span>Czekamy na oceny: <b>{p.judges_missing.join(", ")}</b>. {block ? "Przejście do następnego występu będzie możliwe po komplecie ocen." : "Organizator wyłączył blokadę – możesz przejść dalej."}</span>
      </div>
    )}
    <div className="mt-6 flex gap-3">
      <button data-testid="ann-prev-btn" disabled={!hasPrev} onClick={onPrev} className="h-14 sm:h-16 px-4 sm:px-6 rounded-xl border border-white/15 font-semibold flex items-center gap-2 disabled:opacity-30 hover:bg-white/5 active:scale-95 transition-transform shrink-0"><ChevronLeft className="w-6 h-6" /><span className="hidden sm:inline">Poprzedni</span></button>
      <button data-testid="ann-next-btn" disabled={!hasNext} onClick={onNext} className="flex-1 min-w-0 h-14 sm:h-16 px-3 rounded-xl bg-blue-600 hover:bg-blue-500 font-display text-lg sm:text-2xl font-bold uppercase tracking-wide whitespace-nowrap shadow-lg shadow-blue-600/30 flex items-center justify-center gap-1 sm:gap-2 disabled:opacity-30 active:scale-[0.98] transition-transform">Następny<span className="hidden sm:inline"> występ</span><ChevronRight className="w-6 h-6 sm:w-7 sm:h-7" /></button>
    </div>
  </section>
);

const Info = ({ label, value }) => (
  <div><span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block">{label}</span><span className="text-slate-100">{value || "—"}</span></div>
);

const NextCard = ({ p, n, onClick }) => (
  <button data-testid={`ann-next-${n}`} onClick={onClick} className="fly-card p-4 text-left hover:border-blue-500/50 transition-colors">
    <div className="font-mono text-xs text-blue-400">+{n} {p.time && `· ${p.time}`}</div>
    <div className="font-display text-xl font-bold uppercase leading-tight mt-1">{p.name}</div>
    <div className="text-xs text-slate-400 mt-1">{p.category} · {p.age_group}</div>
    <div className="text-xs text-slate-500">{p.school}</div>
    {!p.present && <div className="text-[11px] text-red-300 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />niezarejestrowany</div>}
  </button>
);

const ListRow = ({ p, active, done, onClick }) => (
  <li>
    <button data-testid={`ann-row-${p.id}`} onClick={onClick}
      className={`w-full text-left px-4 py-2.5 rounded-xl border flex items-center gap-4 transition-colors ${active ? "bg-blue-600/20 border-blue-500" : done ? "border-transparent opacity-45 hover:opacity-80" : "border-white/5 bg-[#1C2541] hover:border-blue-500/40"}`}>
      <span className="font-mono text-xs text-slate-500 w-12 shrink-0">{p.time || `#${p.order}`}</span>
      <span className="flex-1 min-w-0">
        <span className="font-semibold block truncate">{p.name}</span>
        <span className="text-xs text-slate-400 block truncate">{p.category} · {p.age_group} · {p.school}</span>
      </span>
      {!p.present && <AlertTriangle className="w-4 h-4 text-red-400 shrink-0" />}
      <span className="font-mono text-xs text-slate-500 shrink-0">{p.judges_scored}/{p.judges_total}</span>
    </button>
  </li>
);
